import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAssetsStore } from '../../hooks/useAssetsStore';
import { AssetUtils } from '../../services/asset/asset-api-service';
import { SearchInput } from '../../components/SearchInput';
import { CreateAssetButton } from '../../components/createAssetButton/CreateAssetButton';
import { useTranslation } from 'react-i18next';
import { useLocationForSorting } from '../../hooks/useLocationForSorting';
import { useAssetSearch } from '../../hooks/useAssetSearch';

import './Home.css';
import { highlightSearchTerm } from '../../utils/SearchUtils';

const AssetView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const { t } = useTranslation();

    const [childSearchTerm, setChildSearchTerm] = useState<string>('');
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const { getAssetById, getChildAssets, getRootAssets } = useAssetsStore();
    const { userCoords, locationError, loadingLocation } = useLocationForSorting();

    const selectedAsset = id ? getAssetById(parseInt(id)) : null;
    const assets = getRootAssets;

    // Get child assets
    const childAssets = useMemo(() => {
        if (!selectedAsset) return [];
        return getChildAssets(selectedAsset.id);
    }, [selectedAsset, getChildAssets]);

    // Filter child assets based on search term
    const filteredChildAssets = useAssetSearch(childAssets, childSearchTerm, userCoords);

    const handleChildAssetClick = (childAsset: any) => {
        const searchParams = new URLSearchParams(location.search);
        const currentParents = searchParams.get('parents');

        let parentChain = selectedAsset!.id.toString();
        if (currentParents) {
            parentChain = `${currentParents},${parentChain}`;
        }

        navigate(`/assets/${childAsset.id}?parents=${parentChain}`);
    };

    const handleBackClick = () => {
        const searchParams = new URLSearchParams(location.search);
        const parentsParam = searchParams.get('parents');

        if (parentsParam) {
            const parentIds = parentsParam.split(',');
            const immediateParent = parentIds.pop();

            if (parentIds.length > 0) {
                navigate(`/assets/${immediateParent}?parents=${parentIds.join(',')}`);
            } else {
                navigate(`/assets/${immediateParent}`);
            }
        } else {
            navigate('/');
        }
    };

    const handleViewDetails = () => {
        const searchParams = new URLSearchParams(location.search);
        const parentsParam = searchParams.get('parents');

        const detailUrl = parentsParam
            ? `/assets/${selectedAsset!.id}/detail?parents=${parentsParam}`
            : `/assets/${selectedAsset!.id}/detail`;

        navigate(detailUrl);
    };

    if (!selectedAsset) {
        return <div>Asset not found</div>;
    }

    const displayInfo = AssetUtils.formatAssetForDisplay(selectedAsset);

    return (
        <div className="view-assets-container">
            {message && (
                <div className={message.type === 'success' ? 'success' : 'error'}>
                    {message.text}
                </div>
            )}

            {/* location error display */}
            {locationError && (
                <div className="warning">
                    {locationError}
                </div>
            )}

            {/* Asset Header */}
            <div className="asset-detail-header">
                    <button onClick={handleBackClick} className="back-to-list-button">
                        <svg xmlns="http://www.w3.org/2000/svg"
                            width="24" height="24" viewBox="0 0 24 24"
                            fill="none" stroke="#000" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12"></line>
                            <polyline points="12 19 5 12 12 5"></polyline>
                        </svg>
                    </button>
                    <div className="asset-text">
                        <h3 className="asset-title">{displayInfo.title}</h3>
                        <div className="asset-type-badge">{displayInfo.type}</div>
                    </div>
            </div>

            {/* Asset Overview */}
            <button className="button" onClick={handleViewDetails}>
                {t('admin:assets.detailsTab', 'Details')}
            </button>

            {/* Child Search */}
            {getChildAssets(selectedAsset.id).length > 0 && (
                <>
                    <SearchInput
                        value={childSearchTerm}
                        onChange={setChildSearchTerm}
                        placeholder={t('itemSearch', 'Search')}
                        className="search-input"
                        showResultsCount={true}
                        resultsCount={filteredChildAssets.length}
                        totalCount={childAssets.length}
                    />

                    {loadingLocation && !childSearchTerm && (
                        <p className="loading-location">
                            {t('gettingLocation', 'Getting your location for distance sorting...')}
                        </p>
                    )}
                </>
            )}

            {/* Child Assets List */}
            {getChildAssets(selectedAsset.id).length > 0 ? (
                filteredChildAssets.length > 0 ? (
                    <div className="asset-list">
                        {filteredChildAssets.map((childAsset) => {
                            const fullChild = assets.find((a) => a.id === childAsset.id) || childAsset;
                            const childDisplayInfo = AssetUtils.formatAssetForDisplay(fullChild);
                            const showDistance = userCoords &&
                                typeof childAsset.distanceFromUser === 'number' &&
                                childAsset.distanceFromUser !== Infinity &&
                                !isNaN(childAsset.distanceFromUser);

                            return (
                                <div
                                    key={fullChild.id}
                                    className="asset-simple-item"
                                    onClick={() => handleChildAssetClick(fullChild)}
                                >

                                    <i className={`icon-style ${fullChild.assetType.icon}`} ></i>
                                    <div className="asset-content">
                                        <div className="asset-simple-name">
                                            {highlightSearchTerm(childDisplayInfo.title, childSearchTerm)}
                                            {showDistance && (
                                                <span className="distance-badge">
                                                    {fullChild.formattedDistance}
                                                </span>
                                            )}
                                        </div>
                                        <div className="asset-simple-type">
                                            {childDisplayInfo.type}
                                        </div>
                                    </div>

                                    {getChildAssets(fullChild.id).length > 0 && (
                                        <div className="has-children-indicator">
                                            <img
                                                src="/img/hierarchy.svg"
                                                alt="Has child assets"
                                                className="hierarchy-icon"
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="no-assets">
                        {t('noAssetsFoundMatching', 'No child assets found matching')} "{childSearchTerm}"
                    </p>
                )
            ) : (
                <p className="no-assets">{t('common:noChildAssets', 'This asset has no child assets')}.</p>
            )}

            <CreateAssetButton parentId={selectedAsset.id} />
        </div>
    );
};

export default AssetView;
